// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { useGetLink, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps, ButtonVariants } from "@insite/mobius/Button";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getIsNonPurchasable } from "@keller/App/Store/Selectors/GeneralSelectors";
import { color, xScreen } from "@keller/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    itemsList = "itemsList",
    buttonLabel = "buttonLabel",
    buttonVariant = "buttonVariant",
    buttonLink = "buttonLink",
}

interface OwnProps {
    fields: {
        [fields.buttonLabel]: string;
        [fields.buttonVariant]: ButtonVariants;
        [fields.buttonLink]: LinkFieldValue;
        [fields.itemsList]: {
            fields: {
                title: string;
                imageUrl: string;
                linkTitle: string;
                link: LinkFieldValue;
                type: "productGroups" | "openQuotes" | "openInvoices";
            };
        }[];
    };
}

type Props = OwnProps & WidgetProps;

interface UserQuickLinksStyles {
    wrapper?: InjectableCss;
    infoSection?: InjectableCss;
    titleSection?: InjectableCss;
    itemsSection?: InjectableCss;
    title?: TypographyPresentationProps;
    subtitle?: TypographyPresentationProps;
    button?: ButtonPresentationProps;
    blockContent?: InjectableCss;
    blockContentInfo?: InjectableCss;
    item?: {
        title?: TypographyPresentationProps;
        subtitle?: TypographyPresentationProps;
        image?: LazyImageProps;
        link?: LinkPresentationProps;
    };
}

const UserQuickLinksStyles: UserQuickLinksStyles = {
    wrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            padding: 20px;
            border: 1px solid ${color("common.border")};
            border-radius: 4px;
            background-color: ${color("common.white")};
            box-shadow: ${getProp("theme.shadows.4")};
            gap: 30px 60px;
        `,
    },
    infoSection: {
        css: css`
            display: flex;
            align-items: flex-start;
            justify-content: flex-start;
            flex-direction: column;
            min-width: 268px;
            gap: 24px;
            ${xScreen("lte", "md")} {
                flex-direction: row;
                align-items: flex-end;
                justify-content: space-between;
                width: 100%;
            }
            ${xScreen("lte", "xs")} {
                flex-direction: column;
                align-items: flex-start;
                justify-content: flex-start;
                width: 100%;
            }
        `,
    },
    title: {
        variant: "h4",
        css: css`
            margin-bottom: 12px;
        `,
    },
    subtitle: {
        variant: "h3",
        css: css`
            margin-bottom: 0;
        `,
    },
    button: {
        mergeCss: true,
        css: css`
            padding: 12px 36px;
            width: 200px;
        `,
    },
    itemsSection: {
        css: css`
            flex-grow: 1;
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 16px;
            ${xScreen("lte", 767)} {
                flex-direction: column;
                gap: 24px;
            }
        `,
    },
    blockContent: {
        css: css`
            flex: 1 0 216px;
            max-width: 290px;
            border-top: 2px solid ${color("common.ui11")};
            padding-top: 24px;
            display: flex;
            align-items: flex-start;
            ${xScreen("lte", 767)} {
                flex: 1;
                max-width: 100%;
                width: 100%;
            }
        `,
    },
    blockContentInfo: {
        css: css`
            display: flex;
            align-items: flex-start;
            flex-direction: column;
        `,
    },
    item: {
        title: {
            variant: "h5",
            size: 18,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 22px;
                margin-bottom: 14px;
            `,
        },
        subtitle: {
            size: 15,
            color: "common.ui7",
            css: css`
                display: block;
                letter-spacing: 0;
                line-height: 24px;
                margin-bottom: 10px;
                > ${TypographyStyle} {
                    margin-right: 8px;
                }
            `,
        },
        link: {
            typographyProps: {
                size: 12,
                weight: "bold",
            },
        },
        image: {
            css: css`
                flex-shrink: 0;
                margin-right: 20px;
                &,
                & img {
                    height: 68px;
                    width: 68px;
                }
            `,
        },
    },
};

const styles = UserQuickLinksStyles;

const defaultTitle = {
    productGroups: "Product Groups",
    openQuotes: "Open Quotes",
    openInvoices: "Open Invoices",
};

const UserQuickLinks: FC<Props> = ({ fields }) => {
    const { firstName, availableList } = useAppSelector(state => {
        const session = getSession(state);
        return {
            firstName: state.context.session.firstName,
            availableList: {
                productGroups: session.properties.numOfProductGroups || 0,
                openQuotes: session.properties.numOfOpenQuotes || 0,
                openInvoices: session.properties.numOfOpenInvoices || 0,
            },
        };
    });
    const buttonLink = useGetLink(fields.buttonLink);
    const { links: itemsLinks } = useGetLinks(fields.itemsList, o => o.fields.link);
    const history = useHistory();

    const onClick = (url?: string) => url && history.push(url);
    const isNonPurchasable = useAppSelector(state => getIsNonPurchasable(state));

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="UserQuickLinks">
            <StyledWrapper {...styles.infoSection}>
                <StyledWrapper {...styles.titleSection}>
                    <Typography {...styles.title} data-test-selector="UserQuickLinks_title">
                        {translate("Welcome")},
                    </Typography>
                    <Typography {...styles.subtitle} data-test-selector="UserQuickLinks_subtitle">
                        {firstName}
                    </Typography>
                </StyledWrapper>
                <Button {...styles.button} onClick={() => onClick(buttonLink?.url)} variant={fields.buttonVariant}>
                    {fields.buttonLabel}
                </Button>
            </StyledWrapper>
            <StyledWrapper {...styles.itemsSection}>
                {fields.itemsList?.map((itemField, index) => {
                    const item = {
                        title: itemField.fields.title || translate(defaultTitle[itemField.fields.type]),
                        numOfAvailable: availableList[itemField.fields.type] || 0,
                        linkTitle: itemField.fields.linkTitle,
                        imageUrl: itemField.fields.imageUrl,
                        link: itemsLinks?.[index].url || "",
                    };

                    return (
                        <StyledWrapper {...styles.blockContent} key={item.title}>
                            <LazyImage src={item.imageUrl} altText={item.title} {...styles.item?.image} />
                            <StyledWrapper>
                                <Typography {...styles.item?.title}>{item.title}</Typography>
                                {/* xng-changes : Hide openInvoices & openQuotes details on web-only */}
                                {isNonPurchasable &&
                                (item.title === defaultTitle.openInvoices || item.title === defaultTitle.openQuotes) ? (
                                    <Typography size={15} color="text.main">
                                        {siteMessage("User_Quick_Lines_Web_Only_message")}
                                    </Typography>
                                ) : (
                                    <>
                                        <Typography {...styles.item?.subtitle}>
                                            <Typography weight="bold" size={20} color="text.main">
                                                {item.numOfAvailable}
                                            </Typography>
                                            {translate("Available")}
                                        </Typography>
                                        <StyledWrapper>
                                            <Link href={item.link} {...styles.item?.link}>
                                                {item.linkTitle}
                                            </Link>
                                        </StyledWrapper>
                                    </>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: UserQuickLinks,
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                fieldType: "General",
                defaultValue: "View My Dashboard",
            },
            {
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                fieldType: "General",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.buttonVariant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                hideEmptyOption: true,
                defaultValue: "primary",
            },
            {
                name: "itemsList",
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "General",
                isVisible: item => !item.fields.isAuthenticated,
                fieldDefinitions: [
                    {
                        name: "type",
                        displayName: "Type",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Product Groups", value: "productGroups" },
                            { displayName: "Open Quotes", value: "openQuotes" },
                            { displayName: "Open Invoices", value: "openInvoices" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "productGroups",
                    },
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "imageUrl",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "linkTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Url", value: "" },
                        allowCategories: item => false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
